import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Code, Copy, Check, FolderOpen } from 'lucide-react';

const CodeLibrary = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const codeLibrary = [
    {
      category: '🖼️ Bilder-Generierung',
      items: [
        {
          id: 'translate-de-en',
          name: 'Deutsch → Englisch Übersetzung',
          description: 'Übersetzt langen deutschen Prompt zu Englisch für Astria. Nutzt Backend-API (POST) statt Google URL (GET) um Längen-Limit zu umgehen.',
          file: 'IDCardDashboard.js',
          fullPath: '/var/www/frontend/src/components/IDCardDashboard.js',
          githubPath: 'frontend/src/components/IDCardDashboard.js',
          line: '870-930',
          date: '2026-02-25',
          code: `// FIX 2026-02-25: IMMER übersetzen wenn User auf Deutsch ist!
// FIX 2026-02-25 V2: Backend-API nutzen (POST) statt Google URL (GET)
// Problem: Google Translate URL hat Längen-Limit → lange Texte werden abgeschnitten!
const translateToEnglish = async (text) => {
  if (!text || !text.trim()) return "";
  
  // Prüfe ob User aktuell auf Deutsch ist (Toggle-State)
  const userIsOnGerman = isPromptGermanRef.current;
  
  if (!userIsOnGerman) {
    // User ist auf Englisch → Text ist bereits Englisch
    console.log('🌐 Skip translation - User is on English');
    return text;
  }
  
  // User ist auf Deutsch → IMMER übersetzen via Backend API!
  console.log('🌐 User is on German → translating to English via Backend API...');
  console.log('🌐 Text length:', text.length, 'chars');
  
  try {
    // Backend API nutzen (POST, kein URL-Längen-Limit!)
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(\`\${API_URL}/translate\`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: text,
        source_lang: 'de',
        target_lang: 'en'
      })
    });
    
    if (!res.ok) {
      console.warn('Backend translation failed, trying Google fallback...');
      // Fallback auf Google (für kurze Texte)
      const googleUrl = \`https://translate.googleapis.com/translate_a/single?client=gtx&sl=de&tl=en&dt=t&q=\${encodeURIComponent(text.substring(0, 2000))}\`;
      const googleRes = await fetch(googleUrl);
      if (googleRes.ok) {
        const googleData = await googleRes.json();
        return googleData[0]?.[0]?.[0] || text;
      }
      return text;
    }
    
    const data = await res.json();
    const translated = data.translated_text || data.translated || text;
    
    console.log('🌐 Translation complete:', text.substring(0, 50) + '...', '→', translated.substring(0, 50) + '...');
    console.log('🌐 Translated length:', translated.length, 'chars');
    
    return translated;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Fallback auf Original
  }
};`
        }
      ]
    },
    {
      category: '🎬 Video-Generierung (Komplett)',
      items: [
        {
          id: 'video-generator-fullscreen',
          name: 'VideoGeneratorFullscreen (994 Zeilen)',
          description: 'Komplette Video-Generator Komponente aus Gallery.js. Enthält: State, Templates laden, handleGenerate, Poll-Logic, Silent Preload, UI.',
          file: 'Gallery.js',
          fullPath: '/var/www/frontend/src/components/Gallery.js',
          githubPath: 'frontend/src/components/Gallery.js',
          line: '507-1500',
          date: '2026-02-26',
          code: `const VideoGeneratorFullscreen = ({ image, onClose, onGenerate, avatarId, existingVideo, onToggleFavorite, onDelete, onVideoCompleted, onVideoReady, preloadedPhotos = [] }) => {
  const [selectedMotions, setSelectedMotions] = useState([]);
  const [playingPreviewId, setPlayingPreviewId] = useState(null); // Aktuell spielendes Preview-Video
  const [previewFullscreen, setPreviewFullscreen] = useState(null); // {id, url, label} für Fullscreen
  // Template-Prompt als Initial-Wert wenn vorhanden
  const [customPrompt, setCustomPrompt] = useState(image?.templatePrompt || existingVideo?.video_prompt || '');
  const [isGenerating, setIsGenerating] = useState(false);
  // Wenn existingVideo, starte direkt mit dem Video
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(existingVideo?.video_url || null);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  // Video Settings
  const [selectedModel, setSelectedModel] = useState('grok');
  const [videoDuration, setVideoDuration] = useState(7);
  const [videoResolution, setVideoResolution] = useState('720p');
  const [videoAspectRatio, setVideoAspectRatio] = useState('9:16');
  const [endFrameUrl, setEndFrameUrl] = useState(null);
  const [endFramePreview, setEndFramePreview] = useState(null);
  const [isUploadingEndFrame, setIsUploadingEndFrame] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activePrompt, setActivePrompt] = useState(''); // Prompt der gerade generiert wird
  
  // PixVerse Spezial-Features
  const [selectedCameraMovement, setSelectedCameraMovement] = useState('zoom_in');
  const [selectedVoice, setSelectedVoice] = useState('Auto');
  const [lipsyncText, setLipsyncText] = useState('');
  const [selectedEffect, setSelectedEffect] = useState('Kiss Me AI');
  const [selectedSwapMode, setSelectedSwapMode] = useState('person');
  const [videoInputUrl, setVideoInputUrl] = useState('');
  const [availableVideos, setAvailableVideos] = useState([]); // Videos für Lipsync-Picker
  const [availablePhotos, setAvailablePhotos] = useState([]); // Fotos für Standard-Modelle (Foto-Galerie)
  const [selectedSourceImage, setSelectedSourceImage] = useState(null); // Überschreibt image.url wenn User anderes Foto wählt
  const [imageAspectWarning, setImageAspectWarning] = useState(false); // Warnung wenn Bild nicht 9:16
  const [playingVoice, setPlayingVoice] = useState(null); // Aktuell spielende Stimmen-Vorschau
  const [talkingText, setTalkingText] = useState(''); // Text für Talking-Chip (Grok spricht diesen Text)
  const [isOptimizingTTS, setIsOptimizingTTS] = useState(false); // Loading state für TTS-Optimierung
  const voiceAudioRef = useRef(null); // Audio-Element für Stimmen-Preview
  
  // 🎬 DYNAMISCHE VIDEO-TEMPLATES (aus Admin Panel)
  const [videoTemplates, setVideoTemplates] = useState({
    motion: [],
    trend: [],
    camera: [],
    global_prompt: '',
    base_motion: '',
    talking_lipsync_prompt: '',
    models: {}
  });
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [sectionTitles, setSectionTitles] = useState({
    motion: 'Bewegung',
    camera: '🎥 Kamerafahrt',
    trends: '🌟 Trendeffekte',
    models: 'KI-Modell'
  });
  const [promptSettings, setPromptSettings] = useState({
    label: 'Beschreibung',
    placeholder: 'Was soll im Video passieren? z.B. lächelt und winkt in die Kamera'
  });
  
  // 🎬 Templates + Section Titles + Prompt Settings aus Admin Panel laden
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await fetch('/api/video-templates');
        if (response.ok) {
          const data = await response.json();
          setVideoTemplates(data);
          setTemplatesLoaded(true);
          console.log('🎬 Video templates loaded from Admin Panel:', data.motion?.length, 'motion,', data.trend?.length, 'trend,', data.camera?.length, 'camera,', Object.keys(data.models || {}).length, 'models');
        }
      } catch (error) {
        console.error('Error loading video templates:', error);
      }
    };
    const loadSectionTitles = async () => {
      try {
        const response = await fetch('/api/site-settings/section-titles');
        if (response.ok) {
          const data = await response.json();
          setSectionTitles(data);
          console.log('🏷️ Section titles loaded:', data);
        }
      } catch (error) {
        console.error('Error loading section titles:', error);
      }
    };
    const loadPromptSettings = async () => {
      try {
        const response = await fetch('/api/site-settings/prompt-settings');
        if (response.ok) {
          const data = await response.json();
          setPromptSettings(data);
          console.log('✏️ Prompt settings loaded:', data);
        }
      } catch (error) {
        console.error('Error loading prompt settings:', error);
      }
    };
    loadTemplates();
    loadSectionTitles();
    loadPromptSettings();
  }, []);
  
  // 🎥 Merge Admin Panel Modell-Config mit hardcoded VIDEO_MODELS
  const getModelConfig = (modelKey) => {
    const baseConfig = VIDEO_MODELS[modelKey];
    const adminConfig = videoTemplates.models?.[modelKey];
    if (!baseConfig) return null;
    if (!adminConfig) return baseConfig;
    // Admin Panel überschreibt: enabled, label, price, description
    return {
      ...baseConfig,
      label: adminConfig.label || baseConfig.label,
      price: adminConfig.price || baseConfig.price,
      description: adminConfig.description || baseConfig.description,
      enabled: adminConfig.enabled !== false
    };
  };
  
  // Aktive Modelle (nur enabled)
  const activeModels = Object.keys(VIDEO_MODELS).filter(key => {
    const config = getModelConfig(key);
    return config && config.enabled !== false;
  });
  
  // Aktives Modell aus Config (mit Admin Panel Overrides)
  const activeModel = getModelConfig(selectedModel) || VIDEO_MODELS[selectedModel];
  
  // Format time helper (0:00)
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return \`\${mins}:\${secs.toString().padStart(2, '0')}\`;
  };
  
  // Model-Wechsel Handler - aktualisiert Duration/Resolution automatisch
  const handleModelChange = (modelKey) => {
    setSelectedModel(modelKey);
    const model = VIDEO_MODELS[modelKey];
    
    // Duration anpassen - je nach Typ
    if (model.duration.fixed !== undefined) {
      setVideoDuration(model.duration.fixed || 5);
    } else if (model.duration.options) {
      // Bei options: Default oder erster Wert
      setVideoDuration(model.duration.default || model.duration.options[0]);
    } else if (model.duration.default) {
      setVideoDuration(model.duration.default);
    }
    
    // Resolution auf erstes verfügbares setzen
    setVideoResolution(model.resolutions[0]);
    
    // Aspect Ratio auf erstes verfügbares setzen (oder auto als Default)
    if (model.aspectRatios && model.aspectRatios.length > 0) {
      setVideoAspectRatio(model.aspectRatios[0]);
    } else {
      setVideoAspectRatio('auto');
    }
    
    // End-Frame nur für Transition behalten
    if (!model.needsEndFrame) {
      setEndFrameUrl(null);
      setEndFramePreview(null);
    }
    
    // Defaults für PixVerse Spezial-Features setzen
    if (model.cameraMovements) {
      setSelectedCameraMovement(model.cameraMovements[0].id);
    }
    if (model.voices) {
      setSelectedVoice(model.voices[0].id);
    }
    if (model.effects) {
      setSelectedEffect(model.effects[0].id);
    }
    if (model.swapModes) {
      setSelectedSwapMode(model.swapModes[0].id);
    }
  };
  
  // End-Frame Upload Handler (für PixVerse Transition)
  const handleEndFrameUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploadingEndFrame(true);
    
    // Preview sofort zeigen
    const previewUrl = URL.createObjectURL(file);
    setEndFramePreview(previewUrl);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch('/api/upload/temp', {
        method: 'POST',
        headers: { 'Authorization': \`Bearer \${token}\` },
        body: formData
      });
      
      const data = await response.json();
      if (data.url) {
        setEndFrameUrl(data.url);
      } else {
        throw new Error('Upload fehlgeschlagen');
      }
    } catch (err) {
      console.error('End-Frame Upload Fehler:', err);
      setEndFramePreview(null);
      setStatusMessage('Bild-Upload fehlgeschlagen');
    } finally {
      setIsUploadingEndFrame(false);
    }
  };

  // Load favorite status for existingVideo
  useEffect(() => {
    if (existingVideo?.id) {
      const checkFavorite = async () => {
        try {
          const token = localStorage.getItem('snapshot_token');
          const response = await fetch('/api/favorites/list?item_type=video', {
            headers: { 'Authorization': \`Bearer \${token}\` }
          });
          if (response.ok) {
            const data = await response.json();
            console.log('🌟 Check favorite for video:', existingVideo.id, 'in', data.videos);
            setIsFavorited(data.videos?.includes(existingVideo.id) || false);
          }
        } catch (e) {
          console.error('Failed to check favorite:', e);
        }
      };
      checkFavorite();
    }
  }, [existingVideo?.id]);
  const [playbackRate, setPlaybackRate] = useState(existingVideo?.original_speed || 1);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isSavingSpeed, setIsSavingSpeed] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Video startet muted (Safari autoplay policy)
  const videoRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const onVideoReadyRef = useRef(onVideoReady); // Closure-Fix!

  // Ref immer aktuell halten
  useEffect(() => { onVideoReadyRef.current = onVideoReady; }, [onVideoReady]);
  
  // Videos für Lipsync laden wenn pixverse_lipsync ausgewählt
  useEffect(() => {
    if (selectedModel === 'pixverse_lipsync' && avatarId) {
      const fetchVideos = async () => {
        try {
          const token = localStorage.getItem('snapshot_token');
          const response = await fetch(\`/api/videos/list?avatar_id=\${avatarId}&limit=500\`, {
            headers: { 'Authorization': \`Bearer \${token}\` }
          });
          if (response.ok) {
            const data = await response.json();
            setAvailableVideos(data.videos?.filter(v => v.status === 'completed' && v.video_url) || []);
          }
        } catch (e) {
          console.error('Failed to fetch videos for lipsync:', e);
        }
      };
      fetchVideos();
    }
  }, [selectedModel, avatarId]);

  // Fotos für Standard-Modelle - SOFORT aus preloadedPhotos wenn vorhanden
  useEffect(() => {
    if (preloadedPhotos && preloadedPhotos.length > 0) {
      // Sofort setzen aus preloaded - kein API-Call nötig!
      setAvailablePhotos(preloadedPhotos);
      console.log('[Foto-Galerie] Preloaded photos:', preloadedPhotos.length);
    }
  }, [preloadedPhotos]);

  // Format-Prüfung: Warnung wenn Bild nicht 9:16
  useEffect(() => {
    const imageUrl = selectedSourceImage || image?.url;
    if (!imageUrl) {
      setImageAspectWarning(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      // 9:16 = 0.5625, Toleranz ±15%
      const is9x16 = ratio >= 0.48 && ratio <= 0.65;
      setImageAspectWarning(!is9x16);
    };
    img.onerror = () => setImageAspectWarning(false);
    img.src = imageUrl;
  }, [selectedSourceImage, image?.url]);

  // KI-OPTIMIERER States
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizedPrompt, setOptimizedPrompt] = useState('');
  const [imageAnalysis, setImageAnalysis] = useState(existingVideo?.image_analysis || '');
  const hasOptimizedRef = useRef(false);

  // ORIGINAL ENGLISCH - wird IMMER an FAL.ai gesendet!
  const [promptEnglish, setPromptEnglish] = useState('');
  const [combinedPromptEN, setCombinedPromptEN] = useState(''); // Englische Version
  const [combinedPromptDE, setCombinedPromptDE] = useState(''); // Deutsche Version
  const [combinedPromptLang, setCombinedPromptLang] = useState('en'); // Aktuelle Anzeige: 'en' oder 'de'
  const [analysisEnglish, setAnalysisEnglish] = useState('');
  const [isTranslated, setIsTranslated] = useState(false); // true = DE angezeigt

  // KI-OPTIMIERER: NICHT automatisch - User muss Button drücken!
  // (Alter Auto-Start entfernt auf Sebastian's Wunsch 2026-02-16)

  const runKiOptimizer = async () => {
    setIsOptimizing(true);
    setStatusMessage('KI analysiert Bild...');
    setImageAnalysis('');
    setCustomPrompt('');

    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch('/api/beatleap/analyze-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': \`Bearer \${token}\`
        },
        body: JSON.stringify({
          image_url: selectedSourceImage || image?.url
        })
      });

      const data = await response.json();

      if (data.success) {
        // BEIDE Felder setzen!
        setImageAnalysis(data.image_analysis);
        setOptimizedPrompt(data.movement_prompt_en);
        setCustomPrompt(data.movement_prompt_en);

        // ORIGINAL ENGLISCH speichern - für FAL.ai!
        setPromptEnglish(data.movement_prompt_en);
        setAnalysisEnglish(data.image_analysis);
        setIsTranslated(false); // Zeigt aktuell Englisch

        setStatusMessage(\`Analyse: \${data.analysis_words} Wörter | Bewegung: \${data.movement_words} Wörter\`);
      } else {
        setStatusMessage('KI-Optimierer nicht verfügbar');
      }
    } catch (err) {
      console.error('KI-Optimierer Fehler:', err);
      setStatusMessage('Fehler bei der Analyse');
    } finally {
      setIsOptimizing(false);
    }
  };

  // 🎬 DYNAMISCHE MOTION TEMPLATES (aus Admin Panel)
  const motionTemplates = videoTemplates.motion?.length > 0 ? videoTemplates.motion : [
    { id: 'talking', label: '🎤 Talking', prompt: 'PERSON SPEAKING AND TALKING DIRECTLY TO CAMERA', isTalking: true },
    { id: 'smile', label: 'Lächeln', prompt: 'GENTLE WARM SMILE FORMING NATURALLY' },
  ];

  // 🔥 DYNAMISCHE TREND EFFEKTE (aus Admin Panel)
  const trendEffects = videoTemplates.trend?.length > 0 ? videoTemplates.trend : [
    { id: 'glitter_rain', label: 'Glitter Regen', prompt: 'GOLDEN GLITTER PARTICLES raining down' },
  ];

  // 🎥 DYNAMISCHE KAMERA TEMPLATES (aus Admin Panel)
  const cameraTemplates = videoTemplates.camera?.length > 0 ? videoTemplates.camera : [
    { id: 'zoom_in', label: 'Zoom Rein', prompt: 'CAMERA ZOOMS IN SLOWLY' },
  ];

  // 🛡️ GLOBALE PROMPTS aus Admin Panel
  const BASE_MOTION = videoTemplates.base_motion ?? '';  // Leer wenn im Admin Panel leer gesetzt
  const FACE_PROTECTION = videoTemplates.global_prompt ?? '';  // Leer wenn im Admin Panel leer gesetzt
  const TALKING_LIPSYNC = videoTemplates.talking_lipsync_prompt || 'PERSON SPEAKING DIRECTLY TO CAMERA WITH CLEAR LIP SYNC, mouth moving naturally forming each word, lips perfectly synchronized with speech audio, visible articulation of every syllable, jaw and mouth movements matching spoken words, realistic talking head animation';

  const toggleMotion = (id) => {
    setSelectedMotions(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const getFullPrompt = (overrideEnglishPrompt = null) => {
    // ═══════════════════════════════════════════════════════════════
    // GOLDENE REIHENFOLGE (2026 Best Practice):
    // 1. Talking + Text (ZUERST - für Lippensync!)
    // 2. Bewegung (Lächeln, Nicken, etc.)
    // 3. Kamera (Zoom, Pan, etc.)
    // 4. Ambient (Umgebungsgeräusche)
    // 5. BASE_MOTION + FACE_PROTECTION
    // ═══════════════════════════════════════════════════════════════
    
    const parts = [];
    
    // Bei Snapshot Spezial Kamerafahrt: Eigene Logik
    const isCamera = activeModel?.needsCameraMovement;
    if (isCamera && selectedCameraMovement) {
      const camPrompt = activeModel.cameraMovements?.find(c => c.id === selectedCameraMovement)?.prompt;
      if (camPrompt) parts.push(camPrompt);
    }
    
    // STANDARD MODELLE: Goldene Reihenfolge
    if (!isCamera) {
      // PRIO 1: Talking STIL (Text geht SEPARAT über talking_text ans Backend - bleibt Deutsch!)
      if (selectedMotions.includes('talking')) {
        // NUR Lipsync-Stil aus Admin Panel, OHNE den deutschen Text (der geht separat!)
        parts.push(TALKING_LIPSYNC);
      }
      
      // PRIO 3: Trend-Effekte (30 Premium TikTok-Style)
      const trendIds = trendEffects.map(t => t.id);
      const trendPrompts = selectedMotions
        .filter(id => trendIds.includes(id))
        .map(id => trendEffects.find(t => t.id === id)?.prompt)
        .filter(Boolean);
      if (trendPrompts.length > 0) {
        parts.push(trendPrompts.join(', '));
      }
      
      // PRIO 4: Bewegungs-Chips (NUR Mimik/Gestik, OHNE talking/ambient/kamera)
      const motionOnlyIds = ['soft', 'smile', 'nod', 'turn', 'blink', 'wind', 'look', 'relax'];
      const motionPrompts = selectedMotions
        .filter(id => motionOnlyIds.includes(id))
        .map(id => motionTemplates.find(m => m.id === id)?.prompt)
        .filter(Boolean);
      if (motionPrompts.length > 0) {
        parts.push(motionPrompts.join(', '));
      }
      
      // PRIO 5: Kamera-Chips (aus cameraTemplates)
      const cameraIds = cameraTemplates.map(c => c.id);
      const cameraPrompts = selectedMotions
        .filter(id => cameraIds.includes(id))
        .map(id => cameraTemplates.find(c => c.id === id)?.prompt)
        .filter(Boolean);
      if (cameraPrompts.length > 0) {
        parts.push(cameraPrompts.join(', '));
      }
      
      // PRIO 5: Ambient (Umgebungsgeräusche)
      if (selectedMotions.includes('ambient')) {
        const ambientPrompt = motionTemplates.find(m => m.id === 'ambient')?.prompt;
        if (ambientPrompt) parts.push(ambientPrompt);
      }
    }
    
    // Custom Prompt (falls vorhanden) - Override hat Priorität für Auto-Translation
    const promptToUse = overrideEnglishPrompt || promptEnglish || customPrompt;
    if (promptToUse?.trim()) {
      parts.push(promptToUse);
    }
    
    // BASE_MOTION + FACE_PROTECTION (nur wenn nicht leer)
    if (BASE_MOTION) parts.push(BASE_MOTION);
    if (FACE_PROTECTION) parts.push(FACE_PROTECTION);
    
    return parts.filter(Boolean).join(', ') || 'subtle movement, gentle expression, minimal movement, maintain facial features';
  };

  // Anzeige-Prompt für User (zeigt Snapshot Spezial Einstellungen)
  const getDisplayPrompt = () => {
    const parts = [];
    
    // Snapshot Spezial: Zeige spezifische Einstellungen
    if (activeModel?.needsEffect && selectedEffect) {
      const effectLabel = activeModel.effects?.find(e => e.id === selectedEffect)?.label || selectedEffect;
      parts.push(\`✨ Effekt: \${effectLabel}\`);
    }
    if (activeModel?.needsCameraMovement && selectedCameraMovement) {
      const camData = activeModel.cameraMovements?.find(c => c.id === selectedCameraMovement);
      const camPrompt = camData?.prompt || camData?.label || selectedCameraMovement;
      parts.push(\`🎥 \${camPrompt}\`);
    }
    if (activeModel?.needsText && lipsyncText) {
      parts.push(\`🗣️ Text: "\${lipsyncText.slice(0, 50)}\${lipsyncText?.length || 0 > 50 ? '...' : ''}"\`);
      if (selectedVoice) {
        const voiceLabel = activeModel.voices?.find(v => v.id === selectedVoice)?.label || selectedVoice;
        parts.push(\`🎤 Stimme: \${voiceLabel}\`);
      }
    }
    if (activeModel?.needsVideoUrl && videoInputUrl) {
      const modeLabel = activeModel.swapModes?.find(m => m.id === selectedSwapMode)?.label || selectedSwapMode;
      parts.push(\`🎭 \${modeLabel}\`);
    }
    
    // Falls Snapshot Spezial Einstellungen, zeige diese
    if (parts.length > 0) {
      // Füge auch Motion-Tags hinzu wenn vorhanden
      if (selectedMotions.length > 0) {
        const motionLabels = selectedMotions.map(id => motionTemplates.find(m => m.id === id)?.label || cameraTemplates.find(c => c.id === id)?.label).filter(Boolean);
        parts.push(\`Bewegung: \${motionLabels.join(', ')}\`);
      }
      return parts.join(' • ');
    }
    
    // Standard: Zeige den technischen Prompt
    return getFullPrompt();
  };

  // Auto-Start wenn von Vorlage kommend
  const autoStartRef = useRef(false);

  // Live-Update des Anzeigefeldes wenn Kamera/Effekt gewählt wird
  useEffect(() => {
    if (!isGenerating && !existingVideo && !generatedVideoUrl) {
      setActivePrompt(getDisplayPrompt());
    }
  }, [selectedCameraMovement, selectedEffect, selectedMotions, selectedModel, lipsyncText, selectedVoice]);

  // Cleanup on unmount + Body Scroll Lock
  useEffect(() => {
    // Lock body scroll when fullscreen is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = \`-\${window.scrollY}px\`;

    // Auto-Start wenn Vorlage gewählt (nur einmal!)
    if (image.autoStart && !autoStartRef.current) {
      autoStartRef.current = true;
      setTimeout(() => handleGenerate(), 100);
    }

    return () => {
      // Restore scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);

      // Cleanup EventSource or Interval
      if (pollIntervalRef.current) {
        if (pollIntervalRef.current.close) pollIntervalRef.current.close();
        else clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Smarter Progress: Backend-Wert + sanfte Interpolation
  const [backendProgress, setBackendProgress] = useState(0);
  const [displayProgress, setDisplayProgress] = useState(0);

  // SSE + sanftes Ticken (fal.ai gibt während Generierung keinen Progress)
  useEffect(() => {
    if (!isGenerating) {
      setDisplayProgress(0);
      setBackendProgress(0);
      return;
    }

    const interval = setInterval(() => {
      setDisplayProgress(prev => {
        if (prev >= 100) return 100;

        // Progress geht NUR vorwärts, wird aber immer langsamer
        let increment;

        if (prev < 30) {
          increment = 0.4;
        } else if (prev < 60) {
          increment = 0.25;
        } else if (prev < 80) {
          increment = 0.1;
        } else if (prev < 90) {
          increment = 0.03;
        } else {
          // Ab 90%: Ultra-langsam aber IMMER vorwärts
          increment = 0.008;
        }

        // Max 98% - die letzten 2% kommen erst wenn Video wirklich da
        return Math.min(98, prev + increment);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isGenerating, backendProgress]);

  // Dynamische Status-Nachrichten während Generierung
  useEffect(() => {
    if (!isGenerating) {
      setStatusMessage('');
      return;
    }

    const messages = [
      { min: 0, max: 15, texts: ['Starte Video-Generierung...', 'Bereite KI vor...', 'Initialisiere...'] },
      { min: 15, max: 35, texts: ['Analysiere Bild...', 'Erkenne Gesichtszüge...', 'Berechne Bewegung...'] },
      { min: 35, max: 55, texts: ['KI generiert Frames...', 'Erstelle Animation...', 'Rendere Bewegung...'] },
      { min: 55, max: 75, texts: ['Video nimmt Form an...', 'Optimiere Details...', 'Verfeinere Übergänge...'] },
      { min: 75, max: 90, texts: ['Fast geschafft...', 'Finalisiere Video...', 'Letzte Optimierungen...'] },
      { min: 90, max: 100, texts: ['Gleich fertig...', 'Video wird geladen...', 'Noch einen Moment...'] }
    ];

    const updateMessage = () => {
      const phase = messages.find(m => displayProgress >= m.min && displayProgress < m.max);
      if (phase) {
        const randomText = phase.texts[Math.floor(Math.random() * phase.texts.length)];
        setStatusMessage(randomText);
      }
    };

    updateMessage();
    const interval = setInterval(updateMessage, 3000);
    return () => clearInterval(interval);
  }, [isGenerating, displayProgress]);

  const handleGenerate = async () => {
    // Hole den aktuellen Prompt (EN bevorzugt!)
    let finalPrompt = combinedPromptEN || getFullPrompt();
    
    // AUTO-TRANSLATE: Falls User auf Deutsch ist und EN leer, übersetze DE → EN
    if (combinedPromptLang === 'de' && combinedPromptDE && !combinedPromptEN) {
      try {
        setStatusMessage('Übersetze Prompt ins Englische...');
        const token = localStorage.getItem('snapshot_token');
        const translateRes = await fetch('/api/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': \`Bearer \${token}\` },
          body: JSON.stringify({ text: combinedPromptDE, source_lang: 'de', target_lang: 'en' })
        });
        const translateData = await translateRes.json();
        if (translateData.translated_text || translateData.translated) {
          finalPrompt = translateData.translated_text || translateData.translated;
          setCombinedPromptEN(finalPrompt);
          console.log('🌐 Auto-translated to EN for FAL:', finalPrompt);
        }
      } catch (e) {
        console.warn('Translation failed, using DE:', e);
        finalPrompt = combinedPromptDE; // Fallback
      }
    }
    
    // Fallback
    let translatedPrompt = finalPrompt;
    
    // Speichere den aktiven Prompt BEVOR Generierung startet (Anzeige-freundlich)
    setActivePrompt(getDisplayPrompt());
    
    setIsGenerating(true);
    // Start bei 1%, steigt dann flüssig
    setDisplayProgress(1);
    setBackendProgress(1);
    setStatusMessage('Starte...');
    setError(null);

    // Intelligente Vorlage oder normale Animation?
    const isTemplate = image.templatePrompt && image.templateName;
    
    // xAI Direct API? (Test-Button)
    const isXaiDirect = activeModel?.isDirect === true;
    const endpoint = isXaiDirect 
      ? '/api/xai/video/generate'
      : (isTemplate ? '/api/beatleap/animate-template' : '/api/beatleap/animate');

    try {
      const token = localStorage.getItem('snapshot_token');
      
      // xAI Direct: Einfacher Payload
      if (isXaiDirect) {
        const xaiResponse = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': \`Bearer \${token}\`
          },
          body: JSON.stringify({
            image_url: selectedSourceImage || image?.url,
            prompt: finalPrompt || getFullPrompt(),
            duration: videoDuration,
            aspect_ratio: videoAspectRatio || '9:16',
            resolution: videoResolution,
            avatar_id: avatarId
          })
        });
        
        const xaiData = await xaiResponse.json();
        if (!xaiResponse.ok) {
          throw new Error(xaiData.detail || 'xAI API Fehler');
        }
        
        const jobId = xaiData.job_id;
        setStatusMessage('xAI Video wird generiert...');
        
        // Polling für xAI Jobs
        const pollXaiStatus = async () => {
          try {
            const statusResponse = await fetch(\`/api/xai/job/\${jobId}\`, {
              headers: { 'Authorization': \`Bearer \${token}\` }
            });
            const statusData = await statusResponse.json();
            
            if (statusData.status === 'failed') {
              clearInterval(pollIntervalRef.current);
              setError(statusData.error || 'Video-Generierung fehlgeschlagen');
              setIsGenerating(false);
              return;
            }
            
            if (statusData.status === 'completed') {
              clearInterval(pollIntervalRef.current);
              setDisplayProgress(100);
              setStatusMessage('Fertig!');
              
              // Refresh gallery
              if (typeof fetchUserVideos === 'function') {
                await fetchUserVideos();
              }
              
              setIsGenerating(false);
              setShowVideoGenerator(false);
              setActiveTab('videos');
              
              // Toast
              if (typeof addToast === 'function') {
                addToast('🎬 xAI Video fertig!', 'success');
              }
              return;
            }
            
            // Still pending - update progress
            setDisplayProgress(prev => Math.min(prev + 5, 90));
          } catch (err) {
            console.error('xAI Poll error:', err);
          }
        };
        
        // Start polling every 5 seconds
        pollIntervalRef.current = setInterval(pollXaiStatus, 5000);
        pollXaiStatus(); // Initial check
        return;
      }
      
      // Standard fal.ai Flow
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': \`Bearer \${token}\`
        },
        body: isTemplate ? JSON.stringify({
          image_url: selectedSourceImage || image?.url,
          style_template: image.templatePrompt,
          template_name: image.templateName,
          duration: videoDuration,
          resolution: videoResolution,
          model: selectedModel,
          avatar_id: avatarId
        }) : JSON.stringify({
          image_url: selectedSourceImage || image?.url,
          clip_id: image.id,
          auto_mode: false,
          user_prompt: finalPrompt || getFullPrompt(),
          language: 'de',
          image_analysis: analysisEnglish || imageAnalysis || '',
          duration: videoDuration,
          resolution: videoResolution,
          aspect_ratio: videoAspectRatio,
          model: selectedModel,
          end_frame_url: endFrameUrl,
          motion_tags: selectedMotions,
          // Talking Chip - Text separat senden (wird NACH Optimierung hinzugefügt)
          talking_text: selectedMotions.includes('talking') ? talkingText : null,
          // PixVerse Spezial-Features
          camera_movement: activeModel?.needsCameraMovement ? selectedCameraMovement : null,
          voice_id: activeModel?.needsVoice ? selectedVoice : null,
          lipsync_text: activeModel?.needsText ? lipsyncText : null,
          effect: activeModel?.needsEffect ? selectedEffect : null,
          swap_mode: activeModel?.needsVideoUrl ? selectedSwapMode : null,
          video_input_url: (activeModel?.needsVideoUrl || activeModel?.needsVideoInput) ? videoInputUrl : null,
          avatar_id: avatarId
        })
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMsg = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || data);
        throw new Error(errorMsg);
      }

      // POLLING statt SSE - zuverlässiger!
      const jobId = data.jobId || data.job_id;

      const pollStatus = async () => {
        try {
          const statusResponse = await fetch(\`/api/beatleap/animate-status/\${jobId}\`, {
            headers: { 'Authorization': \`Bearer \${token}\` }
          });
          const statusData = await statusResponse.json();
          console.log('🎬 Poll status:', statusData);

          if (statusData.error) {
            clearInterval(pollIntervalRef.current);
            setError(statusData.error);
            setIsGenerating(false);
            return;
          }

          // Backend Progress setzen (Interpolation macht den Rest!)
          if (statusData.progress !== undefined) {
            setBackendProgress(statusData.progress);
          }

          // Status Message
          if (statusData.message) {
            setStatusMessage(statusData.message);
          }

          const videoUrl = statusData.videoUrl || statusData.video_url;

          console.log('🎬 Poll result:', { status: statusData.status, videoUrl: !!videoUrl, hasOnVideoReady: !!onVideoReadyRef.current });

          if (statusData.status === 'completed' && videoUrl) {
            console.log('🎉 VIDEO COMPLETED! Stopping poll...');
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setBackendProgress(100);
            setDisplayProgress(100);
            
            // 🎬 Galerie sofort aktualisieren!
            if (onVideoCompleted) {
              onVideoCompleted();
            }
            
            // 🎬 Direkt ins VideoActionSheet!
            if (onVideoReadyRef.current) {
              // Chip-Labels sammeln
              const chipLabels = selectedMotions.map(id => {
                const motion = motionTemplates.find(m => m.id === id);
                const camera = cameraTemplates.find(c => c.id === id);
                return motion?.label || camera?.label || id;
              }).filter(Boolean);
              
              const videoData = {
                id: statusData.video_id || \`temp-\${Date.now()}\`,
                video_url: videoUrl,
                video_prompt: customPrompt || activePrompt || '',
                source_image_url: selectedSourceImage || image?.url,
                created_at: new Date().toISOString(),
                motion_tags: chipLabels,
                preloaded: true // Video ist schon geladen!
              };
              
              let preloadFired = false;
              // 🎬 SILENT PRELOAD: Video im Hintergrund laden, DANN ActionSheet öffnen
              const preloadVideo = document.createElement("video");
              preloadVideo.src = videoUrl;
              preloadVideo.muted = true;
              preloadVideo.playsInline = true;
              preloadVideo.preload = "auto";
              preloadVideo.style.display = "none";
              document.body.appendChild(preloadVideo);
              
              // Timeout-Fallback (max 15 Sekunden warten)
              const fallbackTimeout = setTimeout(() => {
                if (preloadFired) return;
                preloadFired = true;
                console.log("⏰ Preload Timeout - öffne trotzdem");
                setIsGenerating(false);
                cleanup();
                onVideoReadyRef.current(videoData);
              }, 15000);
              
              const cleanup = () => {
                clearTimeout(fallbackTimeout);
                preloadVideo.pause();
                preloadVideo.oncanplay = null;
                preloadVideo.onerror = null;
                preloadVideo.src = "";
                preloadVideo.remove();
              };
              
              // DER TRIGGER: Erst wenn Video bereit ist!
              preloadVideo.oncanplay = () => {
                if (preloadFired) return;
                preloadFired = true;
                console.log("✅ Video bereit - ActionSheet öffnen!");
                setIsGenerating(false); // Generator JETZT erst schließen
                cleanup();
                onVideoReadyRef.current(videoData);
              };
              
              preloadVideo.onerror = (e) => {
                if (preloadFired) return;
                preloadFired = true;
                console.error("❌ Preload Error", e);
                setIsGenerating(false);
                cleanup();
                onVideoReadyRef.current(videoData);
              };
              
              preloadVideo.load();
            } else {
              // Fallback: Altes Verhalten
              setGeneratedVideoUrl(videoUrl);
            }
          } else if (statusData.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            setError(statusData.message || 'Video-Generierung fehlgeschlagen');
            setIsGenerating(false);
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
          // Bei Netzwerk-Fehler nicht abbrechen, weiter pollen
        }
      };

      // Sofort ersten Poll, dann alle 2 Sekunden
      pollStatus();
      pollIntervalRef.current = setInterval(pollStatus, 2000);

    } catch (err) {
      setError(err.message);
      setIsGenerating(false);
    }
  };

  // GROK-Style: KONSTANTE kleine Dots, Opacity fadet!
  const dotSize = 2; // Konstant 2px wie GROK!
  const dotSpacing = 8; // Konstant 8px Abstand
  // Blur: Startet bei 15px, wird klarer mit Progress
  const blurAmount = isGenerating
    ? Math.max(0, 15 - (displayProgress / 100) * 15)
    : 0;
  // Dot Opacity: 0.6 → 0 (fadet aus während Progress)
  const dotOpacity = isGenerating
    ? Math.max(0, 0.6 - (displayProgress / 100) * 0.6)
    : 0;

  // BOMBENFEST FINAL: Kein Wackeln, kein Verschieben
  const modalRef = useRef(null);
  const scrollYRef = useRef(0);
  
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;
    
    scrollYRef.current = window.scrollY;
    
    // Block ALLE touch moves außer in .modal-scroll-area
    const blockTouch = (e) => {
      const scrollArea = e.target.closest('.modal-scroll-area');
      if (!scrollArea) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    
    // Block auf WINDOW level
    window.addEventListener('touchmove', blockTouch, { passive: false });
    
    // FINAL LOCK: html + body komplett einfrieren
    document.documentElement.style.cssText = \`
      overflow: hidden !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      position: fixed !important;
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      touch-action: none !important;
      overscroll-behavior: none !important;
    \`;
    document.body.style.cssText = \`
      overflow: hidden !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      position: fixed !important;
      top: -\${scrollYRef.current}px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      touch-action: none !important;
      overscroll-behavior: none !important;
      -webkit-overflow-scrolling: auto !important;
    \`;
    
`
        },
        {
          id: 'video-actionsheet',
          name: 'VideoActionSheet.js (742 Zeilen)',
          description: 'Komplettes VideoActionSheet mit Safari Autoplay Fix, Sound-Toggle, Swipe-Navigation, Speed-Control, Aktionen-Menü.',
          file: 'VideoActionSheet.js',
          fullPath: '/var/www/frontend/src/components/VideoActionSheet.js',
          githubPath: 'frontend/src/components/VideoActionSheet.js',
          line: '1-742',
          date: '2026-02-26',
          code: `import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Star, Share2, Download, Trash2, Volume2, VolumeX, Play, Pause } from 'lucide-react';

/**
 * Video Fullscreen - Design wie ImageActionSheet
 */
const VideoActionSheet = ({
  isOpen,
  onClose,
  video,
  videos = [],
  initialIndex = 0,
  onToggleFavorite,
  onDelete,
  favorites = new Set(),
}) => {
  const scrollYRef = useRef(0);
  const closingRef = useRef(false);
  const chipsRef = useRef(null);
  
  // 3 VIDEO REFS - TikTok Style!
  const videoRefs = useRef([null, null, null]); // [prev, current, next]
  const videoRef = useRef(null); // Shortcut für aktuelles
  
  // SIMPEL: Aktuelles Video als State, startet mit prop
  const [currentVideo, setCurrentVideo] = useState(video);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localVideos, setLocalVideos] = useState(videos); // Lokale Kopie für Delete
  const [chipsExpanded, setChipsExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true); // Safari autoplay braucht muted!
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(!video?.preloaded); // Kein Spinner wenn preloaded // Spinner beim Start

  // localVideos NUR beim ÖFFNEN initialisieren, danach nie mehr von außen überschreiben!
  const initializedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      setLocalVideos(videos);
      initializedRef.current = true;
    }
    if (!isOpen) {
      initializedRef.current = false; // Reset beim Schließen
    }
  }, [isOpen, videos]);

  // Anzahl Videos für Navigation (aus lokalem State!)
  const totalVideos = localVideos.length > 0 ? localVideos.length : 1;
  
  // Beim Öffnen: Video und Index setzen
  useEffect(() => {
    if (isOpen && video) {
      setCurrentVideo(video);
      // Index finden - berücksichtige Wrapper-Struktur
      if (videos.length > 0) {
        const idx = videos.findIndex(v => {
          const vData = getVideoData(v);
          return vData?.id === video.id || vData?.video_url === video.video_url;
        });
        setCurrentIndex(idx >= 0 ? idx : 0);
      } else {
        setCurrentIndex(0);
      }
      setChipsExpanded(false);
      setPlaybackSpeed(1);
      setIsPlaying(true);
      setIsMuted(true); // Video startet muted (Safari)
    }
  }, [isOpen, video?.id]);

  const isFavorite = currentVideo && favorites.has(currentVideo.id);
  const canGoPrev = localVideos.length > 1 && currentIndex > 0;
  const canGoNext = localVideos.length > 1 && currentIndex < localVideos.length - 1;

  // Navigation - Videos können direkt oder als Wrapper ({type, data}) kommen
  const getVideoData = (v) => {
    if (!v) return null;
    // Wenn es ein Wrapper ist ({type: 'video', data: {...}})
    if (v.type === 'video' && v.data) return v.data;
    // Sonst direkt
    return v;
  };
  
  const goToVideo = (newIndex) => {
    if (localVideos.length > 0 && newIndex >= 0 && newIndex < localVideos.length) {
      const rawVideo = localVideos[newIndex];
      const videoData = getVideoData(rawVideo);
      
      if (videoData && videoData.video_url) {
        // Aktuelles Video pausieren
        if (videoRef.current) {
          videoRef.current.pause();
        }
        
        setCurrentIndex(newIndex);
        setCurrentVideo(videoData);
        setPlaybackSpeed(1);
        setIsPlaying(true);
        setCurrentTime(0);
        setDuration(0);
        
        // Video direkt starten - moov atom am Anfang = sofort spielbar
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.load();
            videoRef.current.play().catch(() => {});
          }
        }, 0);
      }
    }
  };

  const showToast = (msg) => {
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-md text-white rounded-full text-sm z-[1000001]';
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const handleClose = useCallback((e) => {
    if (e) e.preventDefault(); // Verhindert Browser-Navigation
    if (closingRef.current || isDeleting) return;
    closingRef.current = true;
    
    const videoId = currentVideo?.id;
    
    // WICHTIG: Video stoppen und src leeren BEVOR State gelöscht wird
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
      videoRef.current.load(); // Cleanup
    }
    
    setTimeout(() => {
      onClose(videoId);
      closingRef.current = false;
    }, 50);
  }, [onClose, isDeleting, currentVideo?.id]);

  // Video-Daten für Navigation
  const getVideoAtIndex = (idx) => {
    if (idx < 0 || idx >= localVideos.length) return null;
    return getVideoData(localVideos[idx]);
  };
  
  const prevVideo = getVideoAtIndex(currentIndex - 1);
  const nextVideo = getVideoAtIndex(currentIndex + 1);

  // Video beim Öffnen starten
  useEffect(() => {
    if (isOpen && videoRef.current && currentVideo?.video_url) {
      const timer = setTimeout(() => {
        if (videoRef.current) {
          // Safari-Fix: Erst mit Sound versuchen, bei Fehler muted starten
          videoRef.current.play().catch(() => {
            console.log('🔇 Autoplay blocked, trying muted...');
            if (videoRef.current) {
              videoRef.current.muted = true;
              setIsMuted(true);
              videoRef.current.play().catch(() => {});
            }
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, currentVideo?.video_url]);

  // Cleanup beim Unmount - verhindert Crash
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
    };
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    scrollYRef.current = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = \`-\${scrollYRef.current}px\`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
    closingRef.current = false;
    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.style.width = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);

  // Video controls
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const speedButtonsRef = useRef(null);
  
  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) videoRef.current.playbackRate = speed;
    // Direktes DOM-Update für Speed-Buttons
    if (speedButtonsRef.current) {
      const buttons = speedButtonsRef.current.querySelectorAll('button');
      buttons.forEach(btn => {
        const btnSpeed = parseFloat(btn.dataset.speed);
        if (btnSpeed === speed) {
          btn.style.backgroundColor = 'rgba(34, 197, 94, 0.3)'; // green-500/30
          btn.style.color = 'rgba(34, 197, 94, 1)';
        } else {
          btn.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          btn.style.color = 'rgba(255, 255, 255, 0.8)';
        }
      });
    }
  };

  const soundButtonRef = useRef(null);
  
  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !videoRef.current.muted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      // Direktes DOM-Update für Sound-Icon
      if (soundButtonRef.current) {
        const icon = soundButtonRef.current.querySelector('svg');
        if (icon) {
          // Wechsel zwischen VolumeX und Volume2 durch Opacity
          soundButtonRef.current.dataset.muted = newMuted ? 'true' : 'false';
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (!currentVideo || !videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!currentVideo || !videoRef.current) return;
    setDuration(videoRef.current.duration);
    videoRef.current.playbackRate = playbackSpeed;
  };

  const handleSeek = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (clientX - rect.left) / rect.width;
    if (videoRef.current && duration) {
      videoRef.current.currentTime = percent * duration;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return \`\${mins}:\${secs.toString().padStart(2, '0')}\`;
  };

  // Actions
  const handleShare = async () => {
    if (!currentVideo) return;
    try {
      // Erst versuchen Video als Datei zu teilen (wie bei Bildern)
      if (navigator.canShare) {
        try {
          showToast('Video wird vorbereitet...');
          const response = await fetch(currentVideo.video_url, { mode: 'cors' });
          const blob = await response.blob();
          const fileName = \`SnapshotPro-Video-\${Date.now()}.mp4\`;
          const file = new File([blob], fileName, { type: 'video/mp4' });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ 
              files: [file], 
              title: 'Mein KI-Video',
              text: '🎬 Schau dir mein KI-Video an! Erstellt mit SnapshotPro.ai'
            });
            return;
          }
        } catch (fetchErr) {
          console.log('Video file share not possible, trying URL share');
        }
      }
      
      // Fallback: URL teilen
      if (navigator.share) {
        await navigator.share({ 
          title: 'Mein KI-Video', 
          text: '🎬 Schau dir mein KI-Video an!',
          url: currentVideo.video_url 
        });
      } else {
        await navigator.clipboard.writeText(currentVideo.video_url);
        showToast('Link kopiert!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
        // Bei Fehler: Link kopieren als Fallback
        try {
          await navigator.clipboard.writeText(currentVideo.video_url);
          showToast('Link kopiert!');
        } catch {
          showToast('Bitte manuell teilen');
        }
      }
    }
  };

  const handleDownload = async () => {
    if (!currentVideo) return;
    try {
      const response = await fetch(currentVideo.video_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = \`video_\${currentVideo.id || Date.now()}.mp4\`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Download gestartet...');
    } catch (err) {
      showToast('Download fehlgeschlagen');
    }
  };

  const handleFavorite = () => {
    if (onToggleFavorite && currentVideo) onToggleFavorite(currentVideo.id);
  };

  const handleDelete = async () => {
    if (!currentVideo || isDeleting) return;
    setIsDeleting(true);
    try {
      if (onDelete) await onDelete(currentVideo);
      const newVideos = localVideos.filter((_, idx) => idx !== currentIndex);
      
      if (newVideos.length === 0) {
        showToast('Video gelöscht');
        setIsDeleting(false);
        handleClose();
      } else {
        // Neuen Index berechnen
        const newIndex = currentIndex >= newVideos.length ? newVideos.length - 1 : currentIndex;
        
        // Nächstes Video laden
        const nextVideo = newVideos[newIndex];
        const nextVideoData = getVideoData(nextVideo);
        
        setLocalVideos(newVideos);
        setCurrentIndex(newIndex);
        if (nextVideoData) {
          setCurrentVideo(nextVideoData);
        }
        
        showToast('Video gelöscht');
        setIsDeleting(false);
      }
    } catch (err) {
      showToast('Löschen fehlgeschlagen');
      setIsDeleting(false);
    }
  };

  // Toggle Chips (DOM-basiert wegen iOS Safari Bug)
  const toggleChipsMenu = (e) => {
    if (e) e.preventDefault();
    if (!chipsRef.current) return;
    const isCurrentlyOpen = chipsRef.current.style.maxHeight !== '0px';
    const newState = !isCurrentlyOpen;
    setChipsExpanded(newState);
    chipsRef.current.style.maxHeight = newState ? '400px' : '0px';
    chipsRef.current.style.opacity = newState ? '1' : '0';
  };

  if (!isOpen || !currentVideo) return null;

  const content = (
    <div 
      data-video-viewer
      className="fixed inset-0 bg-black"
      style={{
        zIndex: 999999,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        overscrollBehavior: 'none',
      }}
    >
      {/* VIDEO - Fullscreen (KEIN Poster, nur Video!) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <video
          ref={videoRef}
          src={currentVideo.video_url}
          preload="auto"
          autoPlay
          loop
          playsInline
          muted={isMuted}
          className="max-w-full max-h-full object-contain select-none"
          style={{ pointerEvents: 'none' }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={() => setIsLoading(false)}
          onLoadStart={() => !currentVideo?.preloaded && setIsLoading(true)}
        />
        
        {/* Preload per Blob-Cache (JS) - keine versteckten Video-Elemente mehr */}
        
        {/* LOADING SPINNER - auf dem Poster */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* NAVIGATION ARROWS - dezent, KEIN Effekt, stillstehend */}
      {canGoPrev && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToVideo(currentIndex - 1);
          }}
          className="absolute left-3 top-1/2 w-11 h-11 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center z-10 cursor-pointer"
          style={{ 
            transform: 'translateY(-50%)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none'
          }}
        >
          <ChevronLeft className="w-6 h-6 text-white/80 pointer-events-none" />
        </div>
      )}
      {canGoNext && (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToVideo(currentIndex + 1);
          }}
          className="absolute right-3 top-1/2 w-11 h-11 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center z-10 cursor-pointer"
          style={{ 
            transform: 'translateY(-50%)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none'
          }}
        >
          <ChevronRight className="w-6 h-6 text-white/80 pointer-events-none" />
        </div>
      )}

      {/* SOUND TOGGLE - minimalistisch unten rechts */}
      <button
        ref={soundButtonRef}
        type="button"
        onClick={() => {
          if (videoRef.current) {
            const newMuted = !videoRef.current.muted;
            videoRef.current.muted = newMuted;
            setIsMuted(newMuted);
            // Direktes DOM-Update
            if (soundButtonRef.current) {
              const onIcon = soundButtonRef.current.querySelector('[data-icon="on"]');
              const offIcon = soundButtonRef.current.querySelector('[data-icon="off"]');
              if (onIcon) onIcon.style.display = newMuted ? 'none' : 'block';
              if (offIcon) offIcon.style.display = newMuted ? 'block' : 'none';
            }
          }
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          if (videoRef.current) {
            const newMuted = !videoRef.current.muted;
            videoRef.current.muted = newMuted;
            setIsMuted(newMuted);
            // Direktes DOM-Update
            if (soundButtonRef.current) {
              const onIcon = soundButtonRef.current.querySelector('[data-icon="on"]');
              const offIcon = soundButtonRef.current.querySelector('[data-icon="off"]');
              if (onIcon) onIcon.style.display = newMuted ? 'none' : 'block';
              if (offIcon) offIcon.style.display = newMuted ? 'block' : 'none';
            }
          }
        }}
        className="absolute bottom-32 right-4 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center z-10"
        style={{ pointerEvents: 'auto' }}
      >
        <Volume2 data-icon="on" className="w-5 h-5 text-white/70" style={{ display: isMuted ? 'none' : 'block' }} />
        <VolumeX data-icon="off" className="w-5 h-5 text-white/70" style={{ display: isMuted ? 'block' : 'none' }} />
      </button>

      {/* DELETE QUICK ACCESS - unter Sound (nur wenn Aktionen-Menü ZU ist) */}
      {!chipsExpanded && (
        <button
          type="button"
          disabled={isDeleting}
          onClick={handleDelete}
          onTouchEnd={(e) => { e.preventDefault(); handleDelete(); }}
          className="absolute bottom-20 right-4 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center z-10 disabled:opacity-50"
          style={{ pointerEvents: 'auto' }}
        >
          <Trash2 className="w-5 h-5 text-white/70" />
        </button>
      )}

      {/* UI OVERLAY */}
      <div className="absolute inset-0 flex flex-col pointer-events-none">
        
        {/* Header */}
        <div className="p-3 pt-safe flex items-center justify-between pointer-events-auto">
          <button
            type="button"
            onClick={handleClose}
            disabled={isDeleting}
            className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex-1" />
          
          {onToggleFavorite && (
            <button
              type="button"
              onClick={handleFavorite}
              className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center active:scale-95"
            >
              <Star className={\`w-5 h-5 \${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-white'}\`} />
            </button>
          )}
        </div>

        {/* Spacer - Play/Pause */}
        <div className="flex-1 pointer-events-auto cursor-pointer" onClick={togglePlay} />

        {/* Bottom Section - mit edlem Gradient */}
        <div 
          className="pointer-events-auto"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.85) 100%)',
          }}
        >
          
          {/* Expandable Actions */}
          <div 
            ref={chipsRef}
            className="overflow-hidden transition-all duration-300 ease-out px-3 pt-6"
            style={{ maxHeight: '0px', opacity: '0' }}
          >
            {/* Speed Controls */}
            <div ref={speedButtonsRef} className="flex justify-center gap-2 mb-3">
              {[0.5, 1, 1.25, 1.5, 2].map(speed => (
                <button
                  key={speed}
                  data-speed={speed}
                  onTouchEnd={(e) => { e.preventDefault(); handleSpeedChange(speed); }}
                  onClick={() => handleSpeedChange(speed)}
                  className="px-3 py-2 rounded-xl text-sm font-medium backdrop-blur-sm"
                  style={{
                    backgroundColor: speed === 1 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                    color: speed === 1 ? 'rgba(34, 197, 94, 1)' : 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  {speed}x
                </button>
              ))}
            </div>

            {/* Action Buttons - 3 Buttons (Sound ist jetzt im Video) */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              <ActionButton 
                icon={Download} 
                label="Speichern"
                onTouchEnd={(e) => { e.preventDefault(); handleDownload(); }}
                onClick={handleDownload}
              />
              <ActionButton 
                icon={Share2} 
                label="Teilen"
                onTouchEnd={(e) => { e.preventDefault(); handleShare(); }}
                onClick={handleShare}
              />
              <ActionButton 
                icon={Trash2} 
                label="Löschen"
                danger
                disabled={isDeleting}
                onTouchEnd={(e) => { e.preventDefault(); handleDelete(); }}
                onClick={handleDelete}
              />
            </div>

            {/* Metadaten - Datum, Uhrzeit, Chips */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-3">
              {/* Datum & Uhrzeit */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/50 text-xs">Erstellt</span>
                <span className="text-white/90 text-sm">
                  {currentVideo.created_at 
                    ? new Date(currentVideo.created_at).toLocaleString('de-DE', {
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit', 
                        minute: '2-digit'
                      })
                    : 'Gerade eben'}
                </span>
              </div>
              
              {/* Chips (wenn vorhanden) */}
              {currentVideo.motion_tags && currentVideo.motion_tags.length > 0 && (
                <div>
                  <span className="text-white/50 text-xs block mb-1.5">Gewählte Effekte</span>
                  <div className="flex flex-wrap gap-1.5">
                    {currentVideo.motion_tags.map((tag, idx) => (
                      <span 
                        key={idx} 
                        className="px-2 py-0.5 bg-white/10 rounded text-white/80 text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Prompt (wenn vorhanden) - scrollbar */}
            {currentVideo.video_prompt && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mb-3">
                <span className="text-white/50 text-xs block mb-1">Prompt</span>
                <div 
                  className="text-white/90 text-sm overflow-y-auto"
                  style={{ maxHeight: '100px' }}
                >{currentVideo.video_prompt}</div>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="px-4 mb-2 pt-4">
            <div 
              className="relative h-6 flex items-center cursor-pointer"
              onTouchStart={handleSeek}
              onClick={handleSeek}
            >
              <div className="relative w-full h-1 bg-white/20 rounded-full">
                <div 
                  className="absolute h-full bg-white rounded-full"
                  style={{ width: \`\${duration ? (currentTime / duration) * 100 : 0}%\` }}
                />
              </div>
            </div>
            <div className="flex justify-between -mt-1">
              <span className="text-white/60 text-xs">{formatTime(currentTime)}</span>
              <span className="text-white/60 text-xs">{formatTime(duration)}</span>
            </div>
          </div>

          {/* Bottom Bar - Schließen + Counter */}
          <div className="flex items-center justify-center px-3 pb-safe relative">
            <button
              type="button"
              onTouchEnd={toggleChipsMenu}
              onClick={toggleChipsMenu}
              className="px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm flex items-center gap-1.5 active:scale-95"
            >
              <ChevronDown className={\`w-4 h-4 text-white transition-transform \${chipsExpanded ? 'rotate-180' : ''}\`} />
              <span className="text-white text-sm font-medium">
                {chipsExpanded ? 'Schließen' : 'Aktionen'}
              </span>
            </button>
            
            {localVideos.length > 1 && (
              <div className="absolute right-3 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                <span className="text-white text-sm font-medium">{currentIndex + 1} / {localVideos.length}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

// Action Button Component - wie bei ImageActionSheet
const ActionButton = ({ icon: Icon, label, onClick, onTouchEnd, danger = false, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    onTouchEnd={onTouchEnd}
    disabled={disabled}
    className={\`
      flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-xl backdrop-blur-sm transition-all
      \${disabled ? 'opacity-50' : 'active:scale-[0.97]'}
      \${danger ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/90'}
    \`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-xs font-medium">{label}</span>
  </button>
);

export default VideoActionSheet;
`
        },
        {
          id: 'video-prompt-optimizer',
          name: 'video_prompt_optimizer.py (338 Zeilen)',
          description: 'Backend KI-Prompt Optimierung. Zwei-Stufen Analyse: 1) GPT-4o Vision Bildanalyse, 2) Kreative Prompt-Generierung.',
          file: 'video_prompt_optimizer.py',
          fullPath: '/opt/backend/services/video_prompt_optimizer.py',
          githubPath: 'backend/services/video_prompt_optimizer.py',
          line: '1-338',
          date: '2026-02-26',
          code: `"""
Video Prompt Optimizer V4 - GLEICHE BILDANALYSE WIE BILDGENERIERUNG!
1. Exakt gleiche Bildanalyse wie bei Bildgenerierung (server.py)
2. Intelligente Bewegungs-Generierung basierend auf Analyse
"""
import os
import random
import logging
from openai import AsyncOpenAI

logger = logging.getLogger(__name__)
client = None

def get_client():
    global client
    if client is None:
        from dotenv import load_dotenv
        load_dotenv('/opt/backend/.env')
        client = AsyncOpenAI(api_key=os.getenv('OPENAI_API_KEY'))
    return client


# ═══════════════════════════════════════════════════════════════
# AVATAR PROTECTION PREFIX - Wird jedem Prompt vorangestellt!
# ═══════════════════════════════════════════════════════════════
AVATAR_PROTECTION_PREFIX = """Subject with absolute facial consistency, high-fidelity facial features, locked-in identity, razor-sharp eyes, detailed skin texture, maintained likeness throughout the shot, cinematic face lighting. """


# ═══════════════════════════════════════════════════════════════
# STUFE 1: BILDANALYSE - EXAKT WIE BEI BILDGENERIERUNG!
# ═══════════════════════════════════════════════════════════════

IMAGE_ANALYSIS_PROMPT = """You are an expert visual composition analyzer for professional photography recreation.
Your task: Create an extremely detailed, technically precise description of the VISUAL ELEMENTS in this photograph.

NOTE: You are describing a PHOTOGRAPH/ARTWORK, not a real person. Describe it as you would describe a painting or sculpture - focus on artistic and technical elements.

MOST CRITICAL: The POSE and GAZE DIRECTION must be described FIRST and with EXTREME precision!

**POSE & GAZE (DESCRIBE FIRST - ULTRA PRECISE!):**
HEAD POSITION:
- Head rotation: exactly how many degrees turned left or right (0° = straight, 15° = slight turn, 30° = moderate, 45° = 3/4 view, 90° = profile)
- Head tilt: tilted left/right and by how much
- Chin angle: raised up, level, or tucked down

GAZE DIRECTION:
- Where exactly are the eyes looking? (directly at camera, slightly off to left/right, looking down, looking up and away)
- Is it a direct confrontational gaze or soft peripheral gaze?

BODY ORIENTATION:
- Torso angle: facing camera straight, turned 15°/30°/45° left or right
- Shoulder line: level, left shoulder forward, right shoulder dropped, etc.
- Which shoulder is closer to camera?

VISIBLE BODY PARTS:
- What is visible: face only, head and neck, shoulders, chest, arms, hands?
- Arm positions if visible
- Hand positions if visible

**EXPRESSION (PRECISE!):**
- Mouth: closed lips, slightly parted, subtle smile, wide smile, serious/neutral
- Eyes: wide open, relaxed, squinting slightly, intense stare
- Eyebrows: raised, relaxed, furrowed
- Overall mood: confident, seductive, playful, serious, dreamy

**OUTFIT & STYLING:**
- Detailed clothing description (cut, neckline, fit, length)
- Materials and textures (silk, leather, cotton, etc.)
- How fabric drapes, flows, or structures
- Accessories, jewelry, belts
- Footwear details

**HAIR & FEATURES (BE VERY PRECISE WITH COLORS!):**
- EXACT hair color: Use precise terms! 
  - "bright fiery red" NOT "orange" or "ginger"
  - "deep auburn red" NOT "reddish brown"
  - "platinum blonde" NOT "light hair"
  - "jet black" NOT "dark"
- Hairstyle: length, cut (bob, long, pixie), bangs/fringe
- Hair texture: straight, wavy, curly, sleek, messy
- General features WITHOUT describing specific face details

**ENVIRONMENT & BACKGROUND:**
- Setting type (studio, outdoor, indoor)
- Background color, gradient, or scene
- Props or environmental elements
- Floor/surface visible

**CAMERA & COMPOSITION (VERY IMPORTANT!):**
- EXACT camera height: at eye level, below eye level (looking up), above eye level (looking down)
- EXACT framing: extreme close-up (face only), close-up (head+shoulders), medium close-up (chest up), medium shot (waist up), 3/4 shot (knees up), full body
- EXACT camera distance: intimate (very close), normal portrait distance, further back
- Camera tilt if any: straight, Dutch angle
- Depth of field: shallow (blurred background, f/1.4-2.8) or deep (sharp background)
- What percentage of frame the subject fills (e.g., "subject fills 70% of frame")

**LIGHTING (CRITICAL):**
- Light direction: front, side, back, top, 45-degree
- Light quality: soft/diffused or hard/directional
- Key light, fill light, rim light if present
- Shadow placement and intensity
- Highlights on skin, hair, clothing

**COLOR & MOOD:**
- Dominant color palette
- Color temperature (warm/cool)
- Contrast level (high contrast, low key, high key)
- Overall mood and atmosphere
- Cinematic or photographic style reference

**TECHNICAL QUALITY:**
- Resolution and sharpness description
- Bokeh quality if present
- Style reference (fashion photography, editorial, cinematic, etc.)

OUTPUT FORMAT:
Your description MUST start with EXACT pose details. This is critical for recreation!

EXAMPLE FORMAT:
"Close-up portrait, camera at eye level. Head turned 20 degrees to the right with chin slightly lifted. Direct gaze into camera with confident expression. Shoulders visible, left shoulder slightly forward and closer to camera. Subtle closed-lip smile with relaxed eyes. [Then continue with hair, outfit, lighting, background...]"

STRUCTURE:
1. FIRST: Camera framing (close-up/medium/etc) and camera height
2. SECOND: Head position (rotation degrees, tilt, chin angle)  
3. THIRD: Gaze direction (where eyes are looking)
4. FOURTH: Body/shoulder orientation
5. FIFTH: Expression details
6. THEN: Hair, outfit, lighting, background

Be EXTREMELY specific about pose - use degree estimates!
Do NOT describe specific facial features (nose shape, etc) - the AI will use the trained face.
Do NOT write "Create an image" - describe what IS visible.
Aim for 250-350 words with pose details being the most detailed part."""


# ═══════════════════════════════════════════════════════════════
# STUFE 2: INTELLIGENTE BEWEGUNGS-GENERIERUNG
# ═══════════════════════════════════════════════════════════════

MOVEMENT_GENERATOR_PROMPT = """You are an expert video director creating movement prompts for AI video generation.

You receive a detailed IMAGE ANALYSIS of a photograph. Your job is to create a MOVEMENT PROMPT that describes what this person could NATURALLY do from their current position.

**CRITICAL RULES:**

1. **START FROM CURRENT POSE**: 
   - If person is lying down → describe subtle movements from lying position
   - If sitting → describe movements possible while sitting
   - NEVER make them suddenly stand up or change position drastically

2. **PHYSICALLY POSSIBLE MOVEMENTS ONLY**:
   - Consider what body parts are visible
   - Consider the outfit (tight dress = limited movement, flowing fabric = can show movement)
   - Consider the environment (pool lounger = relaxed micro-movements)

3. **FACE & EXPRESSION PRIORITY**:
   - Describe subtle facial movements (blink, smile forming, eyebrow raise)
   - Eyes can move, look in different directions
   - Lips can part slightly, form a smile

4. **ENVIRONMENTAL INTERACTION**:
   - If holding something (glass, phone) → can interact with it
   - If near water/wind → hair and fabric can move
   - Light can shift subtly

5. **CAMERA MOVEMENT**:
   - Subtle push-in or slow pan
   - Focus shifts
   - Keep subject centered (for face consistency)

6. **NEVER DESCRIBE APPEARANCE**:
   - NO hair color, skin tone, outfit details
   - The video will use the ORIGINAL image as reference
   - Only describe MOVEMENT and CHANGES

**OUTPUT FORMAT:**
Write a 80-120 word movement description in English.
Start with the primary movement, then describe secondary movements, then camera/lighting.

**EXAMPLE:**
"Slowly tilts head 10 degrees to the left while a soft breeze catches the loose strands of hair. Eyes blink gently, then gaze shifts slightly to the right before returning to camera. A subtle smile forms, lips parting just enough to suggest contentment. The hand holding the glass rises slightly, bringing it closer. Fabric ripples gently from an unseen breeze. Camera pushes in slowly, maintaining focus on the eyes. Warm sunlight creates shifting highlights on skin as clouds pass overhead."

Now analyze this image description and create the perfect movement prompt:"""


async def analyze_image_detailed(image_url: str) -> str:
    """
    STUFE 1: Bildanalyse - EXAKT WIE BEI BILDGENERIERUNG!
    """
    try:
        c = get_client()
        
        response = await c.chat.completions.create(
            model='gpt-4o',
            messages=[
                {
                    'role': 'system',
                    'content': IMAGE_ANALYSIS_PROMPT
                },
                {
                    'role': 'user',
                    'content': [
                        {
                            'type': 'image_url',
                            'image_url': {'url': image_url}
                        },
                        {
                            'type': 'text',
                            'text': 'Describe the visual composition, lighting, styling, and artistic elements of this photograph for video animation planning:'
                        }
                    ]
                }
            ],
            max_tokens=1000,
            temperature=0.7
        )
        
        analysis = response.choices[0].message.content.strip()
        
        # Check for refusal
        refusal_phrases = ["i can't help", "i cannot help", "i'm sorry", "i am sorry", "can't assist", "cannot assist", "not able to"]
        if any(phrase in analysis.lower() for phrase in refusal_phrases):
            logger.warning(f"⚠️ GPT refused to analyze image, using fallback")
            analysis = "Medium close-up portrait shot at eye level. Subject facing camera with confident direct gaze. Professional studio lighting with soft key light from the front-left, creating gentle shadows. High-end fashion photography style with shallow depth of field."
        
        logger.info(f'✅ STUFE 1 - Bildanalyse: {len(analysis.split())} Wörter')
        return analysis
        
    except Exception as e:
        logger.error(f'❌ Bildanalyse fehlgeschlagen: {e}')
        return "Unable to analyze image. Portrait shot with neutral pose and professional lighting."


async def generate_movement_prompt(image_analysis: str) -> str:
    """
    STUFE 2: Intelligente Bewegungs-Generierung basierend auf Analyse
    """
    try:
        c = get_client()
        
        response = await c.chat.completions.create(
            model='gpt-4o',
            messages=[
                {
                    'role': 'system',
                    'content': MOVEMENT_GENERATOR_PROMPT
                },
                {
                    'role': 'user',
                    'content': f"""IMAGE ANALYSIS:
{image_analysis}

Based on this detailed analysis, create a natural, physically possible movement prompt for video generation."""
                }
            ],
            max_tokens=300,
            temperature=0.8
        )
        
        prompt = response.choices[0].message.content.strip()
        prompt = prompt.strip('"\\'')
        
        logger.info(f'✅ STUFE 2 - Bewegungs-Prompt: {len(prompt.split())} Wörter')
        return prompt
        
    except Exception as e:
        logger.error(f'❌ Bewegungs-Generierung fehlgeschlagen: {e}')
        return "Gentle, natural movement with soft breathing. Eyes blink naturally. Subtle head tilt. Camera slowly pushes in."


async def create_intelligent_prompt(image_url: str, style_template: str = None) -> dict:
    """
    VOLLSTÄNDIGE PIPELINE:
    1. Bildanalyse (wie Bildgenerierung)
    2. Bewegungs-Prompt basierend auf Analyse
    """
    try:
        # STUFE 1: Bildanalyse
        logger.info('🔍 STUFE 1: Starte Bildanalyse...')
        image_analysis = await analyze_image_detailed(image_url)
        
        # STUFE 2: Bewegungs-Prompt
        logger.info('🎬 STUFE 2: Generiere Bewegungs-Prompt...')
        movement_prompt = await generate_movement_prompt(image_analysis)
        
        # Avatar Protection hinzufügen für finalen Prompt
        final_prompt = AVATAR_PROTECTION_PREFIX + movement_prompt
        
        logger.info(f'✅ Pipeline fertig - Analyse: {len(image_analysis.split())} Wörter, Bewegung: {len(movement_prompt.split())} Wörter')
        
        return {
            'success': True,
            'image_analysis': image_analysis,
            'movement_prompt': movement_prompt,
            'final_prompt': final_prompt,
            'analysis_words': len(image_analysis.split()),
            'movement_words': len(movement_prompt.split())
        }
        
    except Exception as e:
        logger.error(f'❌ Pipeline fehlgeschlagen: {e}')
        return {
            'success': False,
            'error': str(e),
            'image_analysis': 'Analysis failed',
            'movement_prompt': 'Gentle, natural movement with cinematic lighting.',
            'final_prompt': AVATAR_PROTECTION_PREFIX + 'Gentle, natural movement with cinematic lighting.'
        }


# ═══════════════════════════════════════════════════════════════
# LEGACY FUNKTIONEN (für Kompatibilität)
# ═══════════════════════════════════════════════════════════════

# Alias für alten Code
async def generate_creative_prompt(image_analysis: str, style_template: str) -> str:
    return await generate_movement_prompt(image_analysis)

PRESETS = [
    "slowly turning head with a gentle smile, soft ambient lighting",
    "subtle head tilt, eyes softly blinking, cinematic mood",
    "gentle breathing motion, slight shoulder movement, warm lighting",
    "hair swaying gently, soft expression, camera slowly pushes in",
    "elegant head turn to the side, graceful movement, studio lighting"
]

async def analyze_image_for_video(image_url: str) -> str:
    return random.choice(PRESETS)

async def optimize_user_prompt(user_input: str, image_url: str = None) -> dict:
    # USER HAT ABSOLUTE HOHEIT - Prompt wird NICHT gekürzt oder verändert!
    # Die Frontend-Chips haben bereits starke, optimierte Prompts.
    # Wir geben den User-Input 1:1 durch.
    return {'en': user_input, 'de': user_input, 'warning': None}

async def optimize_video_prompt(user_input: str, language: str = 'de') -> str:
    result = await optimize_user_prompt(user_input)
    return result['en']
`
        },
        {
          id: 'agent-video-reference',
          name: '🤖 AGENT KURZFASSUNG - Video',
          description: 'Schnellreferenz für KI-Agenten. Alle Dateien, Pfade, wichtige Funktionen und der komplette Video-Flow.',
          file: 'REFERENZ',
          fullPath: 'Für Agenten',
          githubPath: 'Alle Pfade oben',
          line: 'N/A',
          date: '2026-02-26',
          code: `# 🎬 VIDEO-FLOW AGENT KURZFASSUNG

## 📁 DATEIEN & PFADE

### Frontend (React)
Gallery.js → /var/www/frontend/src/components/Gallery.js
  Zeilen 507-1500 = VideoGeneratorFullscreen (994 Zeilen)
  • handleGenerate() → Startet Video-Generierung
  • startPolling(jobId) → Pollt Backend für Status
  • onVideoReadyRef → Closure-Fix für Callback
  • Silent Preload → Video lädt im Hintergrund

VideoActionSheet.js → /var/www/frontend/src/components/VideoActionSheet.js
  742 Zeilen (komplett)
  • isMuted = true → Safari Autoplay Fix
  • isLoading = !video?.preloaded → Kein Spinner wenn preloaded

### Backend (Python/FastAPI)
server.py → /opt/backend/server.py
video_prompt_optimizer.py → /opt/backend/services/video_prompt_optimizer.py
fal_client.py → /opt/backend/services/fal_client.py

## 🔄 DER FLOW
1. USER → handleGenerate()
2. POST /api/video/generate → { job_id }
3. startPolling() → GET /status/{job_id} alle 2s
4. Backend pollt FAL → bei completed → Video downloaden
5. Frontend: SILENT PRELOAD
6. oncanplay → ActionSheet öffnet → Video spielt!

## ⚠️ WICHTIGE FIXES
Safari Autoplay: isMuted = useState(true)
Closure Bug: onVideoReadyRef = useRef(onVideoReady)
Sound-Icon: style={{ display: isMuted ? 'none' : 'block' }}`
        }
      ]
    },
    {
      category: '⭐ Favoriten (Komplett)',
      items: [
        {
          id: 'favorites-frontend',
          name: 'Favoriten Frontend (Gallery.js)',
          description: 'Kompletter Favoriten-Flow im Frontend: State, Load, Toggle, Tab-Filter, Button.',
          file: 'Gallery.js',
          fullPath: '/var/www/frontend/src/components/Gallery.js',
          githubPath: 'frontend/src/components/Gallery.js',
          line: '3476, 3488, 3805, 4415, 4472',
          date: '2026-02-26',
          code: `// ═══════════════════════════════════════════════════════════════
// FAVORITEN-SYSTEM - Kompletter Flow
// Datei: Gallery.js
// ═══════════════════════════════════════════════════════════════

// ══════ 1. STATE ══════
const [favorites, setFavorites] = useState(new Set());

// ══════ 2. LOAD FAVORITES (beim Mount) ══════
useEffect(() => {
  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;
      const response = await fetch('/api/favorites/list', {
        headers: { 'Authorization': \`Bearer \${token}\` }
      });
      if (response.ok) {
        const data = await response.json();
        const allFavorites = [...(data.images || []), ...(data.videos || [])];
        setFavorites(new Set(allFavorites));
      }
    } catch (e) {
      console.error('Failed to load favorites:', e);
    }
  };
  loadFavorites();
}, []);

// ══════ 3. TOGGLE FAVORITE ══════
const toggleFavorite = useCallback(async (itemId, itemType = 'image') => {
  // Optimistic Update
  setFavorites(prev => {
    const newFavorites = new Set(prev);
    if (newFavorites.has(itemId)) {
      newFavorites.delete(itemId);
    } else {
      newFavorites.add(itemId);
    }
    return newFavorites;
  });

  try {
    const token = localStorage.getItem('snapshot_token');
    await fetch('/api/favorites/toggle', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': \`Bearer \${token}\`
      },
      body: JSON.stringify({ item_id: itemId, item_type: itemType })
    });
  } catch (e) {
    // Revert on error
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(itemId)) newFavorites.delete(itemId);
      else newFavorites.add(itemId);
      return newFavorites;
    });
  }
}, []);

// ══════ 4. FAVORITEN TAB FILTER ══════
const filteredAssets = allAssets.filter(asset => {
  if (activeTab === 'favorites') {
    if (asset.type === 'video') return favorites.has(asset.data?.id);
    if (asset.type === 'image') {
      const gen = asset.data;
      return images.some((url, idx) => url && favorites.has(\`\${gen.id}_\${idx}\`));
    }
    return false;
  }
  // ... andere Tabs
});

// ══════ 5. FAVORITEN TAB BUTTON ══════
<button
  onClick={() => setActiveTab('favorites')}
  className={activeTab === 'favorites' ? 'bg-yellow-500 text-white' : '...'}
>
  <Star fill={activeTab === 'favorites' ? 'currentColor' : 'none'} />
  {favorites.size}
</button>`
        },
        {
          id: 'favorites-backend',
          name: 'Favoriten Backend (server.py)',
          description: 'Backend Routes für Favoriten: Toggle und List. MongoDB Collection Schema.',
          file: 'server.py',
          fullPath: '/opt/backend/server.py',
          githubPath: 'backend/server.py',
          line: '2590-2635',
          date: '2026-02-26',
          code: `# FAVORITEN BACKEND ROUTES - server.py

@api_router.post("/favorites/toggle")
async def toggle_favorite(
    item_id: str = Body(..., embed=True),
    item_type: str = Body("image", embed=True),
    authorization: str = Header(None)
):
    user_id = extract_user_id_from_token(authorization)
    existing = await database.db.favorites.find_one({
        "user_id": user_id, "item_id": item_id, "item_type": item_type
    })
    if existing:
        await database.db.favorites.delete_one({"_id": existing["_id"]})
        return {"success": True, "favorited": False}
    await database.db.favorites.insert_one({
        "user_id": user_id, "item_id": item_id, "item_type": item_type,
        "created_at": datetime.now(timezone.utc)
    })
    return {"success": True, "favorited": True}

@api_router.get("/favorites/list")
async def list_favorites(item_type: str = None, authorization: str = Header(None)):
    user_id = extract_user_id_from_token(authorization)
    query = {"user_id": user_id}
    if item_type: query["item_type"] = item_type
    favorites = await database.db.favorites.find(query).to_list(length=1000)
    result = {"images": [], "videos": []}
    for fav in favorites:
        if fav.get("item_type") == "video": result["videos"].append(fav["item_id"])
        else: result["images"].append(fav["item_id"])
    return result

# MongoDB Collection: favorites
# { user_id, item_id, item_type: "image"|"video", created_at }`
        },
        {
          id: 'agent-favorites-reference',
          name: '🤖 AGENT KURZFASSUNG - Favoriten',
          description: 'Schnellreferenz für KI-Agenten. Pfade, API Routes, Flow.',
          file: 'REFERENZ',
          fullPath: 'Für Agenten',
          githubPath: 'Alle Pfade oben',
          line: 'N/A',
          date: '2026-02-26',
          code: `# ⭐ FAVORITEN AGENT KURZFASSUNG

## 📁 DATEIEN
Gallery.js → /var/www/frontend/src/components/Gallery.js
  • Zeile 3476: useState(new Set())
  • Zeile 3488: loadFavorites() → GET /favorites/list
  • Zeile 3805: toggleFavorite() → POST /favorites/toggle
  • Zeile 4415: Tab Filter
  • Zeile 4472: Tab Button

server.py → /opt/backend/server.py
  • Zeile 2590: POST /favorites/toggle
  • Zeile 2612: GET /favorites/list

## 📋 API ROUTES
POST /api/favorites/toggle
  Body: { item_id, item_type }
  
GET /api/favorites/list
  Response: { images: [], videos: [] }

## ⚠️ WICHTIG
• item_id für Bilder: "{gen_id}_{index}"
• item_id für Videos: Video-ID direkt
• Optimistic Update im Frontend`
        }
      ]
    },
    {
      category: '🗑️ Fotos & Videos Löschen',
      items: [
        {
          id: 'delete-frontend',
          name: 'Löschen Frontend (Gallery.js)',
          description: 'Kompletter Delete-Flow: deleteImage, deleteVideo, Multi-Select Delete mit Fade-Out Animation.',
          file: 'Gallery.js',
          fullPath: '/var/www/frontend/src/components/Gallery.js',
          githubPath: 'frontend/src/components/Gallery.js',
          line: '4011, 4194, 3933',
          date: '2026-02-26',
          code: `// ═══════════════════════════════════════════════════════════════
// LÖSCHEN-SYSTEM - Fotos & Videos
// Datei: Gallery.js
// ═══════════════════════════════════════════════════════════════

// ══════ 1. STATE ══════
const [deleting, setDeleting] = useState(null);
const [recentDeletes, setRecentDeletes] = useState(new Set());
const [selectedForDelete, setSelectedForDelete] = useState(new Set()); // Multi-Select
const [isDeletingImages, setIsDeletingImages] = useState(false);
const [isDeletingVideos, setIsDeletingVideos] = useState(false);

// ══════ 2. DELETE IMAGE ══════
const deleteImage = async (imageId, event, generationId, totalImagesInGen) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Duplikat-Schutz
  if (deleting === imageId || recentDeletes.has(imageId)) {
    console.log('⚠️ Delete already in progress');
    return;
  }

  // Bestätigung
  if (!window.confirm('Bild unwiderruflich löschen?')) return;

  setDeleting(imageId);
  setRecentDeletes(prev => new Set([...prev, imageId]));

  // Spinner anzeigen
  const overlay = document.createElement('div');
  const overlay = showLoadingOverlay("Wird gelöscht...");
  // overlay wird automatisch angezeigt

  try {
    let deleteUrl = \`/api/generations/\${imageId}\`;
    if (avatarId) deleteUrl += \`?avatar_id=\${avatarId}\`;

    const response = await apiClient.delete(deleteUrl);
    if (!response.ok) throw new Error('Delete failed');

    hideLoadingOverlay();
    
    // Success Toast
    showToast('Bild gelöscht');

    // Gallery refresh
    if (onDelete) onDelete(imageId);

  } catch (error) {
    hideLoadingOverlay();
    alert('Fehler beim Löschen: ' + error.message);
  } finally {
    setDeleting(null);
    setTimeout(() => {
      setRecentDeletes(prev => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
    }, 5000);
  }
};

// ══════ 3. DELETE VIDEO ══════
const deleteVideo = async (videoId) => {
  if (deleting === \`video_\${videoId}\`) return;

  if (!window.confirm('🎬 Video wirklich löschen?')) return;

  // 🎬 FADE-OUT Animation
  const videoElement = document.getElementById(\`video-card-\${videoId}\`);
  if (videoElement) {
    videoElement.style.transition = 'opacity 0.3s ease-out';
    videoElement.style.opacity = '0';
    setTimeout(() => videoElement.style.display = 'none', 300);
  }

  await new Promise(resolve => setTimeout(resolve, 350));

  // Optimistic Update
  removeFromCache('video', videoId);
  setVideos(prev => {
    const newVideos = { ...prev };
    Object.keys(newVideos).forEach(key => {
      if (newVideos[key].id === videoId) delete newVideos[key];
    });
    return newVideos;
  });

  // API Call im Hintergrund
  try {
    await fetch(\`/api/video/\${videoId}\`, {
      method: 'DELETE',
      headers: { 'Authorization': \`Bearer \${token}\` }
    });
  } catch (error) {
    console.error('Video delete failed:', error);
  }
};

// ══════ 4. MULTI-SELECT DELETE ══════
const deleteSelectedImages = useCallback(async () => {
  if (selectedForDelete.size === 0) return;
  
  const confirmed = window.confirm(
    \`\${selectedForDelete.size} Bild(er) unwiderruflich löschen?\`
  );
  if (!confirmed) return;

  setIsDeletingImages(true);
  
  // Sequentiell löschen (nicht parallel!)
  for (const imageId of selectedForDelete) {
    try {
      await apiClient.delete(\`/api/generations/\${imageId}?avatar_id=\${avatarId}\`);
    } catch (e) {
      console.error('Delete failed:', imageId, e);
    }
  }
  
  setSelectedForDelete(new Set());
  setIsDeletingImages(false);
  if (onDelete) onDelete([...selectedForDelete]);
}, [selectedForDelete, avatarId, onDelete]);`
        },
        {
          id: 'delete-backend',
          name: 'Löschen Backend (server.py) - DSGVO',
          description: 'DSGVO-konforme Delete Routes mit Audit-Log, Favoriten-Cleanup und Avatar-Validation.',
          file: 'server.py',
          fullPath: '/opt/backend/server.py',
          githubPath: 'backend/server.py',
          line: '1574-1720, 2208-2315',
          date: '2026-02-26',
          code: `# ═══════════════════════════════════════════════════════════════
# LÖSCHEN BACKEND ROUTES - DSGVO-KONFORM
# Datei: server.py
# ═══════════════════════════════════════════════════════════════

# ══════ DELETE GENERATION/IMAGE ══════
@api_router.delete("/generations/{gen_id}")
async def delete_generation(
    gen_id: str,
    authorization: str = Header(None),
    avatar_id: Optional[str] = None,
    image_index: Optional[int] = None
):
    """
    DSGVO-konformes Löschen
    - REQUIRES avatar_id (Security: Cross-Avatar-Deletion verhindern)
    - image_index: Nur dieses Bild löschen
    - Letztes Bild → Generation komplett löschen
    """
    user_id = extract_user_id_from_token(authorization)
    generation = await database.get_generation(gen_id)
    
    # Security Checks
    if not generation:
        raise HTTPException(404, "Generation not found")
    if generation.get("user_id") != user_id:
        raise HTTPException(403, "Not your generation")
    
    # Avatar Validation
    if avatar_id:
        training_job = await database.get_training_job(avatar_id)
        if training_job.get("user_id") != user_id:
            raise HTTPException(403, "Not your avatar")
        if str(generation.get("tune_id")) != str(training_job.get("tune_id")):
            raise HTTPException(403, "Image belongs to different avatar")
    
    # Single Image Delete
    if image_index is not None:
        image_urls = generation.get("image_urls", [])
        deleted_url = image_urls.pop(image_index)
        
        # 🛡️ DSGVO: Favoriten löschen
        image_id = f"{gen_id}_{image_index}"
        await database.db.favorites.delete_many({"item_id": image_id})
        
        # 🛡️ DSGVO: Audit-Log
        await database.db.deletion_audit_log.insert_one({
            "action": "IMAGE_DELETED",
            "generation_id": gen_id,
            "image_index": image_index,
            "user_id": user_id,
            "timestamp": datetime.utcnow(),
            "dsgvo_compliant": True
        })
        
        if len(image_urls) == 0:
            # Letztes Bild → Generation löschen
            await database.db.generations.delete_one({"id": gen_id})
            return {"status": "ok", "generation_deleted": True}
        else:
            # Array updaten
            await database.db.generations.update_one(
                {"id": gen_id},
                {"$set": {"image_urls": image_urls}}
            )
            return {"status": "ok", "remaining_images": len(image_urls)}


# ══════ DELETE VIDEO ══════
@api_router.delete("/video/{video_id}")
async def delete_video(video_id: str, authorization: str = Header(None)):
    """
    DSGVO-konformes Video-Löschen
    - Video-Datei löschen
    - Favoriten löschen
    - Audit-Log erstellen
    """
    user_id = extract_user_id_from_token(authorization)
    
    # Suche Video in beiden Collections
    video = await database.db.video_generations.find_one({"id": video_id})
    source_collection = "video_generations"
    
    if not video:
        video = await database.db.animated_clips.find_one({"job_id": video_id})
        source_collection = "animated_clips"
    
    if not video:
        raise HTTPException(404, "Video not found")
    if video.get("user_id") != user_id:
        raise HTTPException(403, "Not your video")
    
    # Video-Datei löschen
    video_path = f"/opt/backend/storage/animations/{video_id}.mp4"
    if os.path.exists(video_path):
        os.remove(video_path)
    
    # DB-Eintrag löschen
    if source_collection == "video_generations":
        await database.db.video_generations.delete_one({"id": video_id})
    else:
        await database.db.animated_clips.delete_one({"job_id": video_id})
    
    # 🛡️ DSGVO: Favoriten löschen
    await database.db.favorites.delete_many({"item_id": video_id})
    
    # 🛡️ DSGVO: Audit-Log
    await database.db.deletion_audit_log.insert_one({
        "action": "VIDEO_DELETED",
        "video_id": video_id,
        "user_id": user_id,
        "timestamp": datetime.utcnow(),
        "dsgvo_compliant": True
    })
    
    return {"success": True, "deleted_id": video_id}


# ═══════════════════════════════════════════════════════════════
# MongoDB Collections für DSGVO
# ═══════════════════════════════════════════════════════════════
# deletion_audit_log: {
#   action: "IMAGE_DELETED" | "VIDEO_DELETED" | "GENERATION_DELETED",
#   generation_id / video_id,
#   user_id,
#   timestamp,
#   dsgvo_compliant: true
# }`
        },
        {
          id: 'agent-delete-reference',
          name: '🤖 AGENT KURZFASSUNG - Löschen',
          description: 'Schnellreferenz für KI-Agenten. Pfade, API Routes, DSGVO-Compliance.',
          file: 'REFERENZ',
          fullPath: 'Für Agenten',
          githubPath: 'Alle Pfade oben',
          line: 'N/A',
          date: '2026-02-26',
          code: `# 🗑️ LÖSCHEN AGENT KURZFASSUNG

## 📁 DATEIEN

### Frontend
Gallery.js → /var/www/frontend/src/components/Gallery.js
  • Zeile 4011: deleteImage()
  • Zeile 4194: deleteVideo()
  • Zeile 3933: deleteSelectedImages() (Multi-Select)
  • State: deleting, recentDeletes, selectedForDelete

### Backend
server.py → /opt/backend/server.py
  • Zeile 1574: DELETE /generations/{gen_id}
  • Zeile 2208: DELETE /video/{video_id}

### Storage
Videos: /opt/backend/storage/animations/{video_id}.mp4
Bilder: Auf Astria CDN (werden nicht lokal gespeichert)

## 📋 API ROUTES

DELETE /api/generations/{gen_id}?avatar_id={avatar_id}&image_index={idx}
  → Einzelnes Bild oder ganze Generation löschen
  → REQUIRES avatar_id (Security!)

DELETE /api/video/{video_id}
  → Video + Datei + Favoriten löschen

## 🛡️ DSGVO

1. Favoriten werden MIT-gelöscht
2. Audit-Log wird erstellt (deletion_audit_log)
3. Dateien werden physisch gelöscht

## ⚠️ WICHTIG

• avatar_id ist PFLICHT bei Bild-Löschung (Cross-Avatar-Protection)
• image_id Format: "{gen_id}_{index}" (z.B. "abc123_0")
• Duplikat-Schutz: recentDeletes Set (5 Sekunden)
• Fade-Out Animation bei Videos vor dem Löschen`
        }
      ]
    },
    {
            category: '🔐 Security',
      items: []
    },
    {
      category: '💳 Payments',
      items: []
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Code className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Code-Bibliothek</h2>
          <p className="text-white/50 text-sm">Bilder + Video + Favoriten + Agent-Kurzfassungen</p>
        </div>
      </div>

      <div className="space-y-4">
        {codeLibrary.map((category, catIndex) => (
          <div key={catIndex} className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <div className="px-4 py-3 bg-white/5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-violet-400" />
                <span className="font-semibold text-white">{category.category}</span>
                <span className="text-white/40 text-sm">({category.items.length} Snippets)</span>
              </div>
            </div>

            {category.items.length > 0 ? (
              <div className="divide-y divide-white/5">
                {category.items.map((item) => (
                  <div key={item.id} className="bg-black/20">
                    <button
                      onClick={() => toggleExpand(item.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {expandedItems[item.id] ? (
                          <ChevronDown className="w-4 h-4 text-violet-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-white/40" />
                        )}
                        <div className="text-left">
                          <div className="font-medium text-white">{item.name}</div>
                          <div className="text-xs text-white/40">
                            {item.file} • Zeile {item.line} • {item.date}
                          </div>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                        ✓ Fertig
                      </span>
                    </button>

                    {expandedItems[item.id] && (
                      <div className="px-4 pb-4">
                        <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                          <p className="text-sm text-blue-300 mb-2">{item.description}</p>
                          <div className="text-xs space-y-1 text-white/50 border-t border-blue-500/20 pt-2 mt-2">
                            <div><span className="text-white/30">Server:</span> <span className="font-mono text-violet-300">{item.fullPath}</span></div>
                            <div><span className="text-white/30">GitHub:</span> <span className="font-mono text-violet-300">{item.githubPath}</span></div>
                          </div>
                        </div>

                        <div className="relative">
                          <button
                            onClick={() => copyToClipboard(item.code, item.id)}
                            className="absolute top-2 right-2 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors z-10"
                          >
                            {copiedId === item.id ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 text-white/60" />
                            )}
                          </button>
                          <pre className="p-4 bg-black/40 rounded-lg overflow-x-auto text-xs max-h-[600px] overflow-y-auto">
                            <code className="text-green-400 font-mono whitespace-pre">
                              {item.code}
                            </code>
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-white/30 text-sm">
                Noch keine Snippets
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CodeLibrary;
